import { FatTransactionsPageAllocationTransactionsQuery } from '@/__generated__/graphql';
import { IInfoIcon } from '@/assets/icons/info-icons/i-info';
import { Tooltip } from '@/components/basicComponents/tooltip';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import DropdownActions from '@/components/table/dropdownActions';
import NoResults from '@/components/table/noResults';
import RedirectTitleCell from '@/components/table/redirectTitleCell';
import { TableBodyWrapper, TableRowCell, TextCell, blinkingFrame } from '@/components/table/styles';
import { TableColumns } from '@/components/table/types';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { dateFormatter, getUTCDate } from '@/shared/utils/date';
import { themeVar } from '@/styles/theme';
import { TenantType } from '@trace-8/trace8-schema';
import { format, isValid as isValidDate } from 'date-fns';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { ModalWindowType } from '../modals/types';
import NextStep from './cell/nextStep';
import { AllocationWithUpdated } from './table';

export const getLastUpdateTimeStamp = (date: Date) => {
  const isLastUpdateDate = isValidDate(date);
  if (!isLastUpdateDate) return '';

  const formattedLastUpdateDate = format(date, 'MMM d, yyyy');
  const formattedLastUpdateTime = format(date, 'h:mm a');
  return `${formattedLastUpdateDate} ${formattedLastUpdateTime}`;
};

interface BodyRowProps {
  data: FatTransactionsPageAllocationTransactionsQuery['allocationTransactions']['allocations'] | undefined;
  columns: TableColumns[];
  currentRow: AllocationWithUpdated | null;
  openPage: (path: string) => void;
  openModalWindow: (type: ModalWindowType, row: AllocationWithUpdated) => void;
  actionsByStatus: (selectedItemName: string, row: AllocationWithUpdated) => void;
}

export const TableBody = ({ columns, data, currentRow, openPage, actionsByStatus, openModalWindow }: BodyRowProps) => {
  const { user } = useAuth();
  const { isTablet } = useResponsive();
  const [recommendationsRows, setRecommendationsRows] = useState<AllocationWithUpdated[]>([]);
  const [selectedRow, setSelectedRow] = useState<string>();

  useEffect(() => {
    if (data === undefined) return;
    setRecommendationsRows(data);
  }, [data]);

  if (!recommendationsRows.length) {
    return <NoResults />;
  }
  return (
    <TableBodyWrapper>
      {recommendationsRows.map((row) => (
        <TableRowWrapper
          key={row.id}
          isTablet={isTablet}
          selected={selectedRow === row.id}
          onMouseEnter={() => isTablet && setSelectedRow(row.id)}
          loading={!!row.updated ? 1 : 0}
        >
          <TableRowCell width={columns[0].width}>
            {user && user.tenant.type === TenantType.PLATFORM_MANAGER && <TextCell>{row.tenant.name}</TextCell>}
          </TableRowCell>
          <TableRowCell width={columns[1].width}>
            <RedirectTitleCell
              id="actionCurioText"
              onClick={() => openPage(`/investors/${row.legalEntity?.family.id}/${row.legalEntity?.id}?backToTitle=Transactions`)}
            >
              {row.legalEntity?.entityName}
            </RedirectTitleCell>
          </TableRowCell>
          <TableRowCell width={columns[2].width}>
            <RedirectTitleCell id="actionCurioText" onClick={() => openPage(`/strategyDetails/${row.investment.id}?backToTitle=Transactions`)}>
              {row.investment.name}
            </RedirectTitleCell>
          </TableRowCell>
          <TableRowCell width={columns[3].width}>
            <TextCell>
              <FormattingTooltip zIndex={1000}>{row.committedCapital}</FormattingTooltip>
            </TextCell>
          </TableRowCell>
          <TableRowCell width={columns[4].width}>
            <TextCell>{row?.investment?.investorDocsDue && getUTCDate(new Date(row.investment.investorDocsDue))}</TextCell>
          </TableRowCell>
          {/* <TableRowCell width={columns[5].width}>
            <TextCell>
              {row.assignee && `${row.assignee.firstName && row.assignee?.firstName} ${row.assignee?.lastName && row.assignee.lastName}`}
            </TextCell>
          </TableRowCell> */}
          <TableRowCell width={columns[5].width}>
            <LastUpdateCellWrapper>
              <Tooltip title={getLastUpdateTimeStamp(new Date(row.lastUpdatedAt))}>
                <RedirectTitleCell onClick={() => openPage(`transactionDetails/${row.id}?backToTitle=Transactions`)}>
                  {dateFormatter(format(new Date(row.lastUpdatedAt), 'yyyy-MM-dd'), 'yyyy-MM-dd')}
                </RedirectTitleCell>
              </Tooltip>
              {!row.decision && row.reason === 'Always Show' && (
                <Tooltip title="Show All">
                  <IInfoIcon style={{ display: 'flex', alignItems: 'center' }} fill={themeVar('context.warning')} />
                </Tooltip>
              )}
            </LastUpdateCellWrapper>
          </TableRowCell>
          <TableRowCell width={columns[6].width}>
            <NextStep status={row.operationsWorkflowTransitionTask.name} />
          </TableRowCell>
          <TableRowCell width={columns[7].width}>
            {!!row?.operationsWorkflowTransitionTask?.availableActions?.length && (
              <DropdownActions
                selected={selectedRow === row.id}
                items={row?.operationsWorkflowTransitionTask?.availableActions?.map((action) => ({
                  name: _.startCase(action),
                  onClick: () => actionsByStatus(action, row)
                }))}
                disabled={!!row.updated}
              />
            )}
          </TableRowCell>
        </TableRowWrapper>
      ))}
    </TableBodyWrapper>
  );
};

const LastUpdateCellWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const TableRowWrapper = styled.div<{
  addAdaptive?: boolean;
  loading?: boolean | number;
  selected: boolean;
  isTablet: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 60px;
  background-color: ${themeVar('layer.1')};
  border-radius: 8px;
  padding: 0 24px;
  gap: 5px;
  opacity: ${({ loading }) => (loading ? 0.5 : 1)};
  pointer-events: ${({ loading }) => (loading ? 'none' : 'auto')};
  animation: ${({ loading }) => (loading ? blinkingFrame : null)} 1.5s infinite;

  @media (max-width: 1200px) {
    padding: 0 ${({ addAdaptive }) => (addAdaptive ? 10 : 24)}px;
  }

  ${({ isTablet, selected }) =>
    isTablet
      ? css`
          #infoWrap {
            ${selected
              ? css`
                  opacity: 1;
                `
              : ''}
          }

          #actionText {
            ${selected
              ? css`
                  color: ${themeVar('font.action')};
                `
              : ''}
          }

          #actionCurioText {
            ${selected
              ? css`
                  color: ${themeVar('font.action')};
                `
              : ''}
          }

          #actionOrangeText {
            ${selected
              ? css`
                  color: #ef9208;
                `
              : ''}
          }

          #tempInfoWrap {
            ${selected
              ? css`
                  display: none;
                `
              : ''}
          }

          #arrowInSelectRow {
            svg {
              path {
                ${selected
                  ? css`
                      fill: #ef9208;
                    `
                  : ''}
              }
            }
          }

          #selector {
            ${selected
              ? css`
                  outline: 1px solid ${themeVar('border.base')} !important;
                  background-color: ${themeVar('layer.1')};
                `
              : ''}
          }

          #selectIcon {
            ${selected
              ? css`
                  opacity: 1;
                `
              : ''}
          }

          ${selected
            ? css`
                background-color: ${themeVar('layer.12')};
                transition: background-color 100ms ease;
              `
            : ''}
        `
      : css`
          &:hover #infoWrap {
            opacity: 1;
          }

          &:hover #actionText {
            color: ${themeVar('font.action')};
          }

          &:hover #actionOrangeText {
            color: #ef9208;
          }

          &:hover #tempInfoWrap {
            display: none;
          }

          &:hover #actionCurioText {
            color: ${themeVar('font.action')};
          }

          &:hover #arrowInSelectRow {
            svg {
              path {
                fill: #ef9208;
              }
            }
          }

          &:hover #selector {
            outline: 1px solid ${themeVar('border.base')} !important;
            background-color: ${themeVar('layer.1')};
          }

          &:hover #selectIcon {
            opacity: 1;
          }

          &:hover {
            background-color: ${themeVar('layer.12')};
            transition: background-color 100ms ease;
          }
        `}
`;

const LoaderWrapper = styled.div`
  margin-left: auto;
`;
